import { useState, useEffect } from 'react';
import styled from "styled-components";
import { useDropzone } from "react-dropzone";
import { FaFile, FaFileUpload, FaTrashAlt } from "react-icons/fa";
import useSnackbar from '../../hooks/useSnackbar'

const Component = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 15px;
  background: #ffffff;
  color: #7e7e7e;
  border: 1px solid #dedede;
  border-radius: 10px;
`

const StyledDropzone = styled.div`
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100px;
  border-radius: 15px;
  border: dashed 1px silver;
  background: rgba(0, 0, 0, 0.03);
`

export default ({
  initialFiles = [],
  maxSize: maxSizeRaw,
  onFilesChanged,
  allowedFileTypes,
  isSingleFile,
}) => {
  const maxSize = maxSizeRaw || 5242880; // 100000000 is what SLED uses which is ~95.37 MB while this default 1048576 is exactly 1 MB
  const maxSizeInMBs = `${Math.round((maxSize / 1048576) * 100) / 100}MB`

  const snackbar = useSnackbar()

  const [files, setFiles] = useState(initialFiles)

  useEffect(() => {
    if (onFilesChanged) {
      onFilesChanged(files)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files])

  const onDropAccepted = (acceptedFiles = []) => {
    let updateFiles = [...files];
    acceptedFiles.forEach((acceptedFile) => {
      const { name } = acceptedFile;
      if (updateFiles.findIndex(({ name: fName }) => fName === name ) < 0) {
        if (isSingleFile) {
          updateFiles = [acceptedFile]
        } else {
          updateFiles.push(acceptedFile)
        }
      }
    });
    setFiles(updateFiles)
  }

  const onDropRejected = (fileRejections) => {
    if (fileRejections.length > 0) {
      const { errors, file } = fileRejections[0];
      const errorMsgs = errors.map(e => {
        if (e.code === 'file-too-large') {
          return `${file.name} is too large. Max size is ${maxSizeInMBs}`;
        } else if (e.code === 'file-invalid-type') {
          return `${file.name} has an invalid file type.`;
        }
        return `${file.name} was rejected: ${e.message}`;
      });

      snackbar.newMessage({ message: errorMsgs.join(' '), severity: 'failure' })
    }
  };

  const allFileTypes = {
      // Images
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/gif': ['.gif'],
      'image/webp': ['.webp'],
      'image/svg+xml': ['.svg'],

      // Videos
      'video/mp4': ['.mp4'],
      'video/webm': ['.webm'],
      'video/ogg': ['.ogg'],
      'video/quicktime': ['.mov'],

      // Documents
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-powerpoint': ['.ppt'],
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
      'text/plain': ['.txt'],
      'text/csv': ['.csv']
  }

  const filteredFileTypes = (allowedFileTypes && allowedFileTypes.length > 0) ? 
    Object.fromEntries(Object.entries(allFileTypes).filter(([k]) => allowedFileTypes.includes(k))) :
    allFileTypes;

  const dropZoneOptions = {
    onDropAccepted,
    onDropRejected,
    accept: filteredFileTypes,
    maxSize: maxSize || 1048576 // 100000000 is what SLED uses which is ~95.37 MB  which default 1048576 is exactly 1 MB
  }

  if (isSingleFile) {
    dropZoneOptions.maxFiles = 1;
  }

  const { getRootProps, getInputProps } = useDropzone(dropZoneOptions);

  const removeFileByIndex = (index) => {
    const updateFiles = [...files];
    updateFiles.splice(index, 1)
    setFiles(updateFiles)
  }

  return (
    <Component>
      <div style={{ fontWeight: 'bold' }}>{'Upload Files'}</div>
      <StyledDropzone {...getRootProps()}>
        <input {...getInputProps()} />
        <div style={{ color: '#7e7e7e', fontSize: '14px', textAlign: 'center' }}>
          <div style={{ fontSize: '28px' }}><FaFileUpload /></div>
          <div>{'Drag and drop or '}<span style={{ color: '#0280c6', textDecoration: 'underline' }}>{'browse'}</span>{' your files here'}</div>
        </div>
      </StyledDropzone>
      {
        files && files.length > 0 &&
        <div style={{ display: 'flex', flexDirection: 'column', rowGap: '5px' }}>
          {
            files.map((file, index) => {
              const { name } = file;
              return (
                <div
                  key={`${name}-${index}`}
                  style={{
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: '#F3FBFF',
                  padding: '10px',
                  border: '1px solid #dedede',
                  borderRadius: '5px'
                  }} 
                >
                  <FaFile style={{ marginRight: '10px' }} />
                  <div style={{ flex: 1 }}>{name}</div>
                  <FaTrashAlt style={{ color: '#f85c5c', cursor: 'pointer' }} onClick={() => removeFileByIndex(index)} />
                </div>
              )
            })
          }
        </div>
      }
    </Component>
  )
}